import { Table, Button, showDialogConfirm, addToast } from '@octano/global-ui';
import { useTranslation } from 'react-i18next';
import { useRef, useCallback, useState } from 'react';
import useColumnActions from '../hooks/useColumnActions';
import useColumns from '../hooks/useColumns';
import { Institution } from '../interfaces/type';
import { useInstitutionsLoader } from './Loaders/InstitutionsLoader';
import { TranslationsKeys } from '../../../../locales/translations';
import InstitutionsModal, {
  InstitutionsModalMethods,
} from './InstitutionsModal';
import useActions from '../hooks/useActions';

function InstitutionsTable() {
  const { t } = useTranslation(TranslationsKeys.CONFIGURATION_INSTITUTIONS);
  const institutionsModalRef = useRef<InstitutionsModalMethods>(null);
  const { data, loading, query, updateQuery } = useInstitutionsLoader();

  const [deleting, setDeleting] = useState(false);

  const { create, update, destroy } = useActions({
    onError: (key, message) => {
      setDeleting(false);
      addToast({
        icon: 'error',
        color: 'danger',
        text: message || t(`modal.${key}.failed`),
      });
    },
    onSuccess: (key) => {
      setDeleting(false);
      addToast({
        icon: 'success',
        color: 'success',
        text: t(`modal.${key}.success`),
      });
      updateQuery({ ...query, page: key === 'update' ? query.page : 1 });
    },
  });

  const onClickAdd = () => {
    institutionsModalRef?.current?.open();
  };
  const onClickEdit = (row: Institution) => {
    institutionsModalRef?.current?.open(row);
  };

  const onClickDelete = useCallback(
    (row: Institution) => {
      showDialogConfirm({
        icon: {
          name: 'warning',
          color: 'warning',
        },
        title: t(`modal.delete.title`),
        subtitle: t(`modal.delete.description`),
        btnConfirm: {
          text: t(`modal.delete.confirm`),
          onConfirm: () => {
            setDeleting(true);
            destroy(row.id);
          },
        },
        btnClose: {
          text: t(`modal.delete.cancel`),
        },
      });
    },
    [t, destroy],
  );

  const columnsActions = useColumnActions({
    onClickEdit,
    onClickDelete,
  });

  const columns = useColumns(columnsActions);

  return (
    <>
      <div className="mb-5">
        <header className="mb-3">
          <div className="mt-3">
            <p className="fs-16 text-light">{t('description')}</p>
          </div>
          <div className="d-flex w-100 justify-content-end">
            <Button
              type="button"
              size="sm"
              icon="plus"
              text={t('create')}
              onClick={onClickAdd}
            />
          </div>
        </header>

        <Table
          columns={columns}
          data={data?.data ?? []}
          isLoadingResults={loading || deleting}
          pagination={{
            currentPage: query.page,
            itemsPerPage: query.items_per_page,
            totalItems: data?.total || 0,
            totalPages: data?.total_pages || 0,
            onChangePage: (nextPage) =>
              updateQuery({ ...query, page: nextPage }),
          }}
        />
      </div>
      <InstitutionsModal
        ref={institutionsModalRef}
        create={create}
        update={update}
      />
    </>
  );
}

export default InstitutionsTable;
